import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { workspacesApi } from "../api/endpoints";
import { SkeletonCard } from "../components/states/Skeleton";
import { ErrorState } from "../components/states/ErrorState";
import { EmptyState } from "../components/states/EmptyState";
import { formatDateTime } from "../lib/format";
import { useAuth } from "../auth/AuthContext";

/**
 * Settings — workspace info, entitlements, members. Read-only for viewers;
 * workspace_admins can add/remove members.
 */
export default function SettingsPage() {
  const { workspaceId } = useParams();
  const { user } = useAuth();
  const [ws, setWs] = useState(null);
  const [members, setMembers] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("viewer");
  const [inviteError, setInviteError] = useState(null);

  const myMembership = (user?.memberships || []).find(
    (m) => m.workspace_id === workspaceId,
  );
  const canManage = myMembership?.role === "workspace_admin";

  async function load() {
    setError(null);
    try {
      const [w, m] = await Promise.all([
        workspacesApi.get(workspaceId),
        workspacesApi.listMembers(workspaceId),
      ]);
      setWs(w);
      setMembers(m);
    } catch (e) {
      setError(e);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  async function onInvite(e) {
    e.preventDefault();
    setInviteError(null);
    setBusy(true);
    try {
      await workspacesApi.addMember(workspaceId, {
        email: inviteEmail,
        role: inviteRole,
      });
      setInviteEmail("");
      setInviteRole("viewer");
      await load();
    } catch (err) {
      setInviteError(err);
    } finally {
      setBusy(false);
    }
  }

  async function onRemove(userId) {
    try {
      await workspacesApi.removeMember(workspaceId, userId);
      await load();
    } catch (err) {
      setError(err);
    }
  }

  if (error && !ws) {
    return <ErrorState error={error} onRetry={load} />;
  }
  if (!ws) return <SkeletonCard />;

  return (
    <div className="space-y-6">
      <header>
        <div className="text-xs uppercase tracking-widest text-slate-500">
          Workspace
        </div>
        <h1 className="mt-1 text-2xl font-semibold text-slate-900">Settings</h1>
      </header>

      <section
        aria-labelledby="ws-info"
        className="rounded border border-slate-200 bg-white p-5"
        data-testid="settings-info"
      >
        <h2 id="ws-info" className="text-sm font-semibold text-slate-900">
          Workspace details
        </h2>
        <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <Row label="Name" value={ws.name} />
          <Row
            label="Plan"
            value={
              <span
                className="inline-flex rounded bg-slate-100 px-2 py-0.5 text-xs uppercase tracking-wider text-slate-700"
                data-testid="ws-plan"
              >
                {ws.plan}
              </span>
            }
          />
          <Row label="Industry" value={ws.industry || "—"} />
          <Row label="Country" value={ws.country || "—"} />
          <Row label="Employees" value={ws.employee_size || "—"} />
          <Row label="BCM maturity" value={ws.bcm_maturity || "—"} />
          <Row label="Created" value={formatDateTime(ws.created_at)} />
          <Row label="Notes" value={ws.notes || "—"} />
        </dl>
      </section>

      <section
        aria-labelledby="ent"
        className="rounded border border-slate-200 bg-white p-5"
        data-testid="settings-entitlements"
      >
        <h2 id="ent" className="text-sm font-semibold text-slate-900">
          Plan limits
        </h2>
        <dl className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
          <Row label="Max processes / import" value={ws.entitlements?.max_processes} />
          <Row label="Max analyses" value={ws.entitlements?.max_analyses} />
          <Row
            label="Features"
            value={
              (ws.entitlements?.features || []).length
                ? (ws.entitlements.features || []).join(", ")
                : "core"
            }
          />
        </dl>
        <p className="mt-3 text-xs text-slate-500">
          Need more? <a href="mailto:sales@recoveryassure.io" className="underline">Contact us</a> to raise the limit or upgrade.
        </p>
      </section>

      <section
        aria-labelledby="members"
        className="rounded border border-slate-200 bg-white p-5"
        data-testid="settings-members"
      >
        <h2 id="members" className="text-sm font-semibold text-slate-900">
          Members
        </h2>
        {members === null ? (
          <div className="mt-3">
            <SkeletonCard />
          </div>
        ) : members.length === 0 ? (
          <EmptyState title="No members yet." />
        ) : (
          <table
            className="mt-3 w-full text-sm"
            data-testid="members-table"
          >
            <thead className="text-left text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="py-2">Email</th>
                <th className="py-2">Role</th>
                <th />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {members.map((m) => (
                <tr key={m.user_id} data-testid={`member-row-${m.user_id}`}>
                  <td className="py-2 text-slate-800">{m.email}</td>
                  <td className="py-2 font-mono text-xs text-slate-700 uppercase">
                    {m.role}
                  </td>
                  <td className="py-2 text-right">
                    {canManage && m.user_id !== user?.id && (
                      <button
                        type="button"
                        onClick={() => onRemove(m.user_id)}
                        className="text-xs text-red-700 hover:underline"
                        data-testid={`member-remove-${m.user_id}`}
                      >
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {canManage && (
          <form
            onSubmit={onInvite}
            className="mt-6 flex flex-wrap items-end gap-3"
            data-testid="invite-form"
          >
            <label className="text-xs text-slate-600">
              <span className="block mb-1 uppercase tracking-wider text-[10px] text-slate-500">
                Invite (email)
              </span>
              <input
                type="email"
                required
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                data-testid="invite-email"
                className="rounded border border-slate-300 bg-white px-2 py-1 text-sm w-64"
              />
            </label>
            <label className="text-xs text-slate-600">
              <span className="block mb-1 uppercase tracking-wider text-[10px] text-slate-500">
                Role
              </span>
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                data-testid="invite-role"
                className="rounded border border-slate-300 bg-white px-2 py-1 text-sm"
              >
                <option value="viewer">viewer</option>
                <option value="analyst">analyst</option>
                <option value="workspace_admin">workspace_admin</option>
              </select>
            </label>
            <button
              type="submit"
              disabled={busy}
              data-testid="invite-submit"
              className="rounded bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60"
            >
              {busy ? "Inviting…" : "Add member"}
            </button>
            {inviteError && (
              <div
                role="alert"
                data-testid="invite-error"
                className="basis-full rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 flex items-center gap-2"
              >
                <code className="rounded bg-red-100 px-1.5 py-0.5 font-mono text-[10px]">
                  {inviteError.code || "ERROR"}
                </code>
                <span>{inviteError.message}</span>
              </div>
            )}
          </form>
        )}
      </section>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wider text-slate-500">
        {label}
      </dt>
      <dd className="mt-0.5 text-slate-900">{value ?? "—"}</dd>
    </div>
  );
}
